import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { SearchIcon, HelpCircleIcon, BookOpenIcon, MessageCircleIcon } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";

const FAQ = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useLanguage();

  const faqData = [
    {
      category: "Geral",
      color: "bg-blue-100 text-blue-800",
      questions: [
        {
          q: "Como abrir um novo chamado?",
          a: "Você pode abrir um chamado através do botão 'Novo Chamado' no dashboard ou acessando diretamente /novo-chamado. Preencha todas as informações solicitadas para agilizar o atendimento."
        },
        {
          q: "Como acompanhar o status do meu chamado?",
          a: "Acesse o dashboard em /chamados para ver todos os seus chamados. Clique em qualquer chamado para ver detalhes, timeline de comunicação e SLA restante. Você também receberá e-mails automáticos a cada mudança de status."
        },
        {
          q: "Qual é o horário de atendimento?",
          a: "Nosso horário padrão é de segunda a sexta, das 8h às 18h. Para chamados críticos, oferecemos suporte 24/7, incluindo finais de semana e feriados."
        },
        {
          q: "Posso reabrir um chamado fechado?",
          a: "Sim, chamados podem ser reabertos em até 48 horas após o fechamento. Após esse período, será necessário abrir um novo chamado."
        }
      ]
    },
    {
      category: "SLA e Prioridades",
      color: "bg-green-100 text-green-800",
      questions: [
        {
          q: "Como é calculada a prioridade do meu chamado?",
          a: "A prioridade é calculada automaticamente baseada na matriz de Impacto x Urgência: Alto+Alto=Crítica, Alto+Médio=Alta, Médio+Médio=Média, etc. Você define esses valores ao abrir o chamado."
        },
        {
          q: "O que acontece se o SLA for ultrapassado?",
          a: "Chamados que excedem 80% do SLA são automaticamente escalados. Se ultrapassado, nossa equipe de supervisão assume e você é notificado com ações corretivas."
        },
        {
          q: "O SLA conta finais de semana?",
          a: "Para chamados não-críticos, o SLA conta apenas horário comercial (seg-sex, 8h-18h). Chamados críticos têm SLA 24/7."
        },
        {
          q: "Quando o SLA é pausado?",
          a: "O SLA é pausado quando: aguardamos sua resposta, chamado está pendente de aprovação externa, ou quando solicitado teste/validação do usuário."
        }
      ]
    },
    {
      category: "Tipos de Chamado",
      color: "bg-purple-100 text-purple-800",
      questions: [
        {
          q: "Qual a diferença entre Incidente e Requisição?",
          a: "Incidente: algo que não está funcionando (ex: e-mail parou). Requisição: pedido de algo novo ou acesso (ex: instalar software, criar usuário)."
        },
        {
          q: "Quando usar 'Problema'?",
          a: "Use Problema quando há incidentes recorrentes ou você suspeita que múltiplos incidentes têm a mesma causa raiz. Ex: lentidão diária no sistema às 14h."
        },
        {
          q: "O que é uma Mudança?",
          a: "Mudança é qualquer alteração planejada em sistemas, processos ou infraestrutura. Ex: atualização de sistema, mudança de configuração de rede."
        },
        {
          q: "Posso anexar arquivos ao chamado?",
          a: "Sim! Você pode anexar prints, documentos (PDF, DOC), logs de erro, etc. Limite de 10MB por arquivo. Isso acelera muito a resolução."
        }
      ]
    },
    {
      category: "Comunicação",
      color: "bg-orange-100 text-orange-800",
      questions: [
        {
          q: "Como me comunicar com o técnico?",
          a: "Use a timeline de comunicação na página do chamado. Suas mensagens vão direto para o técnico responsável, que receberá notificação por e-mail."
        },
        {
          q: "Receberei atualizações por e-mail?",
          a: "Sim! Você recebe e-mail automático quando: chamado é atribuído, status muda, técnico responde, ou SLA está próximo do vencimento."
        },
        {
          q: "Posso alterar o e-mail de notificações?",
          a: "Sim, entre em contato conosco ou inclua outros e-mails na descrição do chamado para incluí-los nas notificações."
        },
        {
          q: "Como avaliar o atendimento?",
          a: "Após a resolução, você receberá um link por e-mail para avaliar. Também pode avaliar diretamente na página do chamado."
        }
      ]
    },
    {
      category: "Problemas Técnicos",
      color: "bg-red-100 text-red-800",
      questions: [
        {
          q: "Não consigo fazer login no sistema",
          a: "Verifique: 1) E-mail e senha corretos, 2) Caps Lock desabilitado, 3) Limpe cache do navegador. Se persistir, abra chamado com print da tela de erro."
        },
        {
          q: "Meu chamado não aparece no dashboard",
          a: "Aguarde alguns minutos para sincronização. Se não aparecer, verifique se está logado com o e-mail correto usado para abrir o chamado."
        },
        {
          q: "Erro ao anexar arquivos",
          a: "Verifique: 1) Arquivo menor que 10MB, 2) Formato suportado (PNG, JPG, PDF, DOC), 3) Conexão estável. Tente um arquivo por vez."
        },
        {
          q: "O sistema está lento",
          a: "Isso pode ser temporário. Tente: 1) Atualizar a página, 2) Limpar cache, 3) Usar outro navegador. Se persistir, abra chamado."
        }
      ]
    }
  ];

  const filteredFAQ = faqData.map(category => ({
    ...category,
    questions: category.questions.filter(item => 
      item.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t.faq.title}</h1>
          <p className="text-gray-600">
            {t.faq.subtitle}
          </p>
        </div>

        {/* Busca */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <SearchIcon className="h-5 w-5 mr-2" />
              {t.faq.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={t.faq.searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            {searchTerm && (
              <p className="text-sm text-gray-600 mt-2">
                {filteredFAQ.reduce((acc, cat) => acc + cat.questions.length, 0)} resultado(s) encontrado(s)
              </p>
            )}
          </CardContent>
        </Card>

        {/* Cards de Acesso Rápido */}
        {!searchTerm && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <HelpCircleIcon className="h-8 w-8 text-blue-600 mb-2" />
                <CardTitle className="text-lg">Como Abrir Chamado</CardTitle>
                <CardDescription>
                  Passo a passo para criar seu primeiro chamado
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <BookOpenIcon className="h-8 w-8 text-green-600 mb-2" />
                <CardTitle className="text-lg">Entender SLA</CardTitle>
                <CardDescription>
                  Saiba como funcionam nossos prazos de atendimento
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <MessageCircleIcon className="h-8 w-8 text-purple-600 mb-2" />
                <CardTitle className="text-lg">Comunicação</CardTitle>
                <CardDescription>
                  Como se comunicar efetivamente com nossa equipe
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        )}

        {/* FAQ por Categoria */}
        <div className="space-y-6">
          {filteredFAQ.map((category) => (
            <Card key={category.category}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{category.category}</span>
                  <Badge className={category.color}>
                    {category.questions.length} pergunta(s)
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {category.questions.map((item, index) => (
                    <AccordionItem key={index} value={`${category.category}-${index}`}>
                      <AccordionTrigger className="text-left">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="text-gray-700 whitespace-pre-line">
                          {item.a}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredFAQ.length === 0 && searchTerm && (
          <Card>
            <CardContent className="text-center py-12">
              <SearchIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhum resultado encontrado
              </h3>
              <p className="text-gray-600 mb-4">
                Não encontramos perguntas relacionadas a "{searchTerm}".
              </p>
              <p className="text-sm text-gray-500">
                Tente usar palavras-chave diferentes ou{" "}
                <a href="/novo-chamado" className="text-blue-600 hover:underline">
                  abra um chamado
                </a>{" "}
                para obter ajuda personalizada.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Contato */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Não encontrou sua resposta?</CardTitle>
            <CardDescription>
              Nossa equipe está pronta para ajudar você
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-2 text-blue-600">Abra um Chamado</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Para dúvidas específicas ou problemas técnicos, abra um chamado para receber atendimento personalizado.
                </p>
                <a 
                  href="/novo-chamado" 
                  className="text-blue-600 hover:underline text-sm font-medium"
                >
                  Criar Novo Chamado →
                </a>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2 text-green-600">Contato Direto</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>📧 suporte@tecitbettersolution.com.br</p>
                  <p>📞 (11) 98546-4552</p>
                  <p>💬 Chat online: seg-sex, 8h-18h</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default FAQ;
